/**
 * Lazy autoplay for background/UGC videos.
 *
 * Videos rendered with class "js-lazy-video" (and preload="none", no autoplay
 * attribute) stay dormant — zero network, zero decode — until they actually
 * enter the viewport. They play on enter and pause on exit, so offscreen
 * videos never compete for bandwidth or CPU. Critical for mobile: the theme
 * previously autoplayed up to 15 videos per page load.
 *
 * Pausing alone does not stop a download: a clip that started playing keeps
 * fetching its read-ahead buffer (~20 s, 10–20 MB at these bitrates) after it's
 * paused. On a phone, scrolling past several clips stacked those fetches up and
 * a tapped link's page request queued behind them. So on exit a video that is
 * still downloading is "parked": its sources are detached and the fetch
 * aborted, then restored at the same timestamp on re-entry. Tapping a link
 * parks every downloading video on the page so navigation gets the connection
 * to itself.
 */
(function () {
  'use strict';

  var io = null;

  function playSafe(video) {
    // Respect an explicit user pause (e.g. rs-ugc-band tap-to-pause).
    if (video.dataset.lazyUserPaused === '1') return;
    // Keep sound on if the user unmuted this video; otherwise mute so
    // programmatic play() is allowed by autoplay policies.
    if (video.dataset.lazyUnmuted !== '1') video.muted = true;
    var p = video.play();
    if (p && typeof p.then === 'function') {
      p.then(function () {
        clearBlocked(video);
      }).catch(function (err) {
        // Autoplay refused — iOS Low Power Mode, data-saver, etc. Show a
        // tap-to-play affordance; a user gesture is allowed even in those modes.
        // AbortError (paused or parked mid-start while scrolling past) is not a block.
        if (err && err.name === 'NotAllowedError') markBlocked(video);
      });
    }
  }

  // Detach the sources and abort the in-flight download. Only acts on a video
  // that is actually downloading — a dormant (preload none) or fully loaded
  // one costs nothing and is left alone.
  function park(video) {
    if (video.dataset.lazyParked === '1') return false;
    if (video.networkState !== video.NETWORK_LOADING) return false;

    video.dataset.lazyWasPlaying = video.paused ? '' : '1';
    video.pause();
    video.dataset.lazyTime = String(video.currentTime || 0);
    if (video.hasAttribute('src')) {
      video.dataset.lazySrc = video.getAttribute('src');
      video.removeAttribute('src');
    }
    Array.prototype.forEach.call(video.querySelectorAll('source[src]'), function (source) {
      source.dataset.lazySrc = source.getAttribute('src');
      source.removeAttribute('src');
    });
    video.dataset.lazyParked = '1';
    video.load(); // no sources left → resets the element and cancels the fetch
    return true;
  }

  function unpark(video) {
    if (video.dataset.lazyParked !== '1') return;
    if (video.dataset.lazySrc) {
      video.setAttribute('src', video.dataset.lazySrc);
      delete video.dataset.lazySrc;
    }
    Array.prototype.forEach.call(video.querySelectorAll('source[data-lazy-src]'), function (source) {
      source.setAttribute('src', source.dataset.lazySrc);
      delete source.dataset.lazySrc;
    });
    delete video.dataset.lazyParked;

    var t = parseFloat(video.dataset.lazyTime) || 0;
    delete video.dataset.lazyTime;
    video.load();
    if (t > 0) {
      video.addEventListener(
        'loadedmetadata',
        function () {
          try {
            video.currentTime = isFinite(video.duration) ? Math.min(t, Math.max(video.duration - 0.1, 0)) : t;
          } catch (e) {}
        },
        { once: true }
      );
    }
  }

  function bindVideo(video) {
    // Section scripts call video.play() directly (e.g. UGC card tap). If that
    // lands on a parked video, restore it first so the play actually loads.
    video.addEventListener('play', function () {
      if (video.dataset.lazyParked !== '1') return;
      unpark(video);
      var p = video.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
    });
    // A paused video can start buffering again on its own; if that happens
    // while it's off screen, cut it off.
    video.addEventListener('progress', function () {
      if (video.dataset.lazyOffscreen === '1' && video.paused) park(video);
    });
  }

  // SVG play triangle for the injected button.
  var PLAY_SVG =
    '<svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';

  function tapToPlay(video) {
    return function (e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      video.muted = true;
      delete video.dataset.lazyUserPaused;
      unpark(video);
      var p = video.play();
      if (p && typeof p.then === 'function') {
        p.then(function () {
          clearBlocked(video);
        }).catch(function () {});
      }
    };
  }

  function markBlocked(video) {
    // UGC band already ships its own centered play icon + card tap-to-play;
    // just flip it into the paused state so the icon shows.
    var ugc = video.closest('.rs-ugc__card');
    if (ugc) {
      ugc.classList.add('is-paused');
      return;
    }
    // Everything else: inject a one-time centered play button over the video.
    if (video.dataset.lazyFallback === '1') return;
    video.dataset.lazyFallback = '1';
    var host = video.parentElement;
    if (!host) return;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'lazy-video-play';
    btn.setAttribute('aria-label', 'Play video');
    btn.innerHTML = PLAY_SVG;
    btn.style.cssText =
      'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);z-index:2;' +
      'width:56px;height:56px;padding:0;border:0;border-radius:999px;cursor:pointer;' +
      'display:flex;align-items:center;justify-content:center;' +
      'background:rgba(0,0,0,0.55);color:#fff;backdrop-filter:blur(2px);';
    var handler = tapToPlay(video);
    btn.addEventListener('click', handler);
    video.addEventListener('click', handler);
    host.appendChild(btn);
  }

  function clearBlocked(video) {
    var ugc = video.closest('.rs-ugc__card');
    if (ugc) ugc.classList.remove('is-paused');
    if (video.dataset.lazyFallback === '1') {
      var host = video.parentElement;
      var btn = host && host.querySelector('.lazy-video-play');
      if (btn) btn.remove();
      video.dataset.lazyFallback = '';
    }
  }

  function onIntersect(entries) {
    entries.forEach(function (entry) {
      var video = entry.target;
      if (entry.isIntersecting) {
        delete video.dataset.lazyOffscreen;
        unpark(video);
        playSafe(video);
      } else {
        video.dataset.lazyOffscreen = '1';
        video.pause();
        park(video);
      }
    });
  }

  function init() {
    var videos = document.querySelectorAll('video.js-lazy-video:not([data-lazy-bound])');
    if (!videos.length) return;

    if (!('IntersectionObserver' in window)) {
      // Ancient fallback: behave like before, just play them.
      videos.forEach(function (v) {
        v.setAttribute('data-lazy-bound', '');
        playSafe(v);
      });
      return;
    }

    io = io || new IntersectionObserver(onIntersect, { rootMargin: '200px 0px', threshold: 0.01 });

    videos.forEach(function (v) {
      v.setAttribute('data-lazy-bound', '');
      bindVideo(v);
      io.observe(v);
    });
  }

  // --- Free the connection when the visitor leaves the page ------------------

  var navParked = [];
  var navParkedAt = 0;
  var RESUME_EVENTS = ['pointerdown', 'touchstart', 'wheel', 'keydown'];

  function isPageNavigation(e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null;
    var a = e.target.closest && e.target.closest('a[href]');
    if (!a || a.hasAttribute('download')) return null;
    var target = a.getAttribute('target');
    if (target && target !== '_self') return null;
    var url;
    try {
      url = new URL(a.href, location.href);
    } catch (err) {
      return null;
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    // Same document, only the hash changes — nothing is loading.
    if (url.origin === location.origin && url.pathname === location.pathname && url.search === location.search && url.hash) return null;
    return a;
  }

  function parkAllForNavigation() {
    Array.prototype.forEach.call(document.querySelectorAll('video'), function (video) {
      if (park(video)) navParked.push(video);
    });
    if (!navParked.length) return;
    // If the navigation never happens (cancelled, or the visitor stays), bring
    // the videos back as soon as they interact with this page again. Not on a
    // timer: on a slow phone connection the next page can take several
    // seconds, and resuming mid-navigation would recreate the jam.
    navParkedAt = Date.now();
    RESUME_EVENTS.forEach(function (type) {
      window.addEventListener(type, onInteractAfterPark, { capture: true, passive: true });
    });
  }

  function onInteractAfterPark() {
    // Ignore the tail of the tap that started the navigation.
    if (Date.now() - navParkedAt < 600) return;
    resumeAfterNavigation();
  }

  function resumeAfterNavigation() {
    RESUME_EVENTS.forEach(function (type) {
      window.removeEventListener(type, onInteractAfterPark, { capture: true });
    });
    var videos = navParked;
    navParked = [];
    videos.forEach(function (video) {
      if (video.classList.contains('js-lazy-video') && io) {
        // Re-observing delivers a fresh entry: on screen → unpark + play,
        // off screen → stays parked.
        io.unobserve(video);
        io.observe(video);
      } else {
        var wasPlaying = video.dataset.lazyWasPlaying === '1';
        unpark(video);
        if (wasPlaying) {
          var p = video.play();
          if (p && typeof p.catch === 'function') p.catch(function () {});
        }
      }
    });
  }

  // Bubble phase on document: runs after section/theme handlers, so a link
  // whose click was taken over (drawer, cart, search) is skipped via defaultPrevented.
  document.addEventListener('click', function (e) {
    if (isPageNavigation(e)) parkAllForNavigation();
  });

  // Back/forward cache restore: the page comes back exactly as it was left.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted && navParked.length) resumeAfterNavigation();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-scan when the theme editor injects/re-renders sections.
  document.addEventListener('shopify:section:load', init);
})();
